import React, {
  useCallback,
  useState,
  useEffect,
  useContext,
  useMemo,
} from 'react';
import {
  Form,
  Input,
  Button,
  Typography,
  Row,
  Col,
  Checkbox,
  Select,
  message,
} from 'antd';
import { useQuery } from '@apollo/client';
import timezones from 'timezones-list';
import GooglePlaceAutocomplete from 'components/GooglePlaceAutocomplete/GooglePlaceAutocomplete';
import CapabilitiesSelect from 'components/Select/CapabilitiesSelect';
import LocationStatusSelect from 'components/Select/LocationStatusSelect';
import { LocationStatuses } from 'constants/LocationStatuses';
import AdminRoles from 'constants/AdminRoles';
import {
  StationMode,
  STATION_MODE_LABELS,
  STATION_MODE_REQUIRED_CAPABILITY_NAMES,
} from 'constants/StationModes';
import { QUERY_LIST_CAPABILITY } from 'queries/DictionariesQueries';
import formatAddress from 'services/formatAddress';
import stripTypename from 'services/stripTypename';
import CorporateClientSelect from '../../../../components/Select/CorporateClientSelect';
import { UserContext } from '../../../../contexts/UserContext';

const { Option } = Select;
const { Title } = Typography;

const LocationForm = ({ handleSubmit, location }: any) => {
  const { dbUser } = useContext(UserContext);
  const [loading, setLoading] = useState(false);
  const mode = location ? 'update' : 'create';
  const [form] = Form.useForm();
  const { data: capabilityData } = useQuery(QUERY_LIST_CAPABILITY);

  // Live-watched so the Station Mode options below narrow as capabilities are (de)selected,
  // before the form is even saved.
  const selectedCapabilityIds: string[] =
    Form.useWatch('capabilities', form) || [];
  const selectedCapabilityNames = useMemo(() => {
    const idToName = new Map(
      (capabilityData?.listCapability || []).map((c: any) => [
        c.capability_id + '',
        c.name,
      ]),
    );
    return new Set(selectedCapabilityIds.map((id) => idToName.get(id)));
  }, [capabilityData, selectedCapabilityIds]);

  useEffect(() => {
    location &&
      form.setFieldsValue({
        name: location.name,
        pretty_name: location.pretty_name,
        status_id: location.status_id ? location.status_id.toString() : null,
        corporate_client_id: location.corporate_client_id
          ? location.corporate_client_id.toString()
          : null,
        low_cup_threshold: location.low_cup_threshold,
        track_inventory: location.track_inventory,
        menu_url: location.menu_url,
        timezone: location.timezone || null,
        stripe_reader_serial: location.stripe_reader_serial || null,
        address: formatAddress(location),
        capabilities: (location.capabilities || []).map(
          (c: any) => c.capability_id + '',
        ),
        station_mode: location.station_config?.mode || null,
      });

    !location &&
      form.setFieldsValue({
        status_id: LocationStatuses.Active.toString(),
        track_inventory: true,
      });

    if (dbUser?.isCorporateAdmin && dbUser?.userCorporateClients?.[0]) {
      form.setFieldsValue({
        corporate_client_id:
          dbUser.userCorporateClients[0]?.corporate_client_id?.toString(),
      });
    }
  }, [form, location, dbUser]);

  // When user changes capability selection in the form, automatically clear station_mode if its required capabilities are missing
  const handleValuesChange = useCallback(
    (changedValues: any) => {
      if ('capabilities' in changedValues && capabilityData?.listCapability) {
        const newCapIds: string[] = changedValues.capabilities || [];
        const idToName = new Map(
          (capabilityData.listCapability || []).map((c: any) => [
            c.capability_id + '',
            c.name,
          ]),
        );
        const newCapNames = new Set(newCapIds.map((id) => idToName.get(id)));
        const currentStationMode = form.getFieldValue('station_mode') as
          | StationMode
          | undefined;

        if (currentStationMode) {
          const requiredCapabilities =
            STATION_MODE_REQUIRED_CAPABILITY_NAMES[currentStationMode];
          if (
            requiredCapabilities &&
            !requiredCapabilities.every((name) => newCapNames.has(name))
          ) {
            form.setFieldsValue({ station_mode: null });
          }
        }
      }
    },
    [capabilityData, form],
  );

  const submit = useCallback(
    async (values: any) => {
      setLoading(true);
      try {
        const {
          address,
          capabilities,
          status_id,
          station_mode,
          ...attributes
        } = values;
        let postalCode = address.addressProperties?.postal_code;

        if (!postalCode) {
          const postalCodeObject = address.raw
            ?.map((r: any) => r.address_components)
            .flat()
            .find((c: any) => c.types.find((t: any) => t === 'postal_code'));

          postalCode = postalCodeObject?.short_name;
        }

        let validStationMode = station_mode || null;
        if (
          validStationMode &&
          STATION_MODE_REQUIRED_CAPABILITY_NAMES[
            validStationMode as keyof typeof STATION_MODE_REQUIRED_CAPABILITY_NAMES
          ]
        ) {
          const required =
            STATION_MODE_REQUIRED_CAPABILITY_NAMES[
              validStationMode as keyof typeof STATION_MODE_REQUIRED_CAPABILITY_NAMES
            ];
          const isValid = required.every((name) =>
            selectedCapabilityNames.has(name),
          );
          if (!isValid) {
            validStationMode = null;
          }
        }

        // Only `mode` is edited here - the per-location overrides (campus_card_available,
        // return_station_display) aren't part of this form yet, but preserve them if a location
        // already has them set some other way, rather than clobbering them on every save.
        const stationConfigOverrides = stripTypename({
          ...(location?.station_config || {}),
        });
        delete stationConfigOverrides.mode;
        delete stationConfigOverrides.__typename;
        const station_config =
          validStationMode || Object.keys(stationConfigOverrides).length > 0
            ? { ...stationConfigOverrides, mode: validStationMode }
            : null;

        await handleSubmit({
          ...attributes,
          status_id,
          station_config,
          activated_on:
            Number(status_id) === LocationStatuses.Active
              ? !location || location?.status_id !== LocationStatuses.Active
                ? new Date()
                : undefined
              : null,
          ...address.addressProperties,
          postal_code: postalCode,
          capabilities,
        });
      } catch (error: any) {
        console.error(error);
        message.error(error?.message || 'Failed to save location');
      } finally {
        setLoading(false);
      }
    },
    [handleSubmit, location, selectedCapabilityNames],
  );

  const handleAddressChange = useCallback(
    ({
      target: {
        value: { name, addressProperties },
      },
    }: any) => {
      const [placeName, ...allOtherParts] = name
        .split(',')
        .map((part: string) => part.trim());
      const generatedName = `${placeName} - ${allOtherParts.join(', ')}`;

      if (
        placeName === addressProperties.address_line_1 ||
        placeName === placeName.address_line_2
      ) {
        form.setFieldsValue({ name });
      } else {
        form.setFieldsValue({ name: generatedName, pretty_name: placeName });
      }
    },
    [form],
  );

  return (
    <Row gutter={10} style={{ paddingLeft: '15px', marginBottom: '10px' }}>
      <Form
        form={form}
        layout="vertical"
        name="location_create"
        onFinish={submit}
        onValuesChange={handleValuesChange}
      >
        <Row>
          <Col span={24}>
            <Title level={2}>
              {mode === 'create'
                ? 'Create Location'
                : `Update Location "${location.pretty_name}"`}
            </Title>
          </Col>
          <Col span={24} style={{ margin: '20px 0' }}>
            <Form.Item
              name="address"
              label="Google maps autocomplete, search for location"
              rules={[{ required: true, message: 'Please select address!' }]}
            >
              <GooglePlaceAutocomplete onChange={handleAddressChange} />
            </Form.Item>

            <Form.Item
              label="Pretty Name"
              name="pretty_name"
              rules={[{ required: true, message: 'Please input name!' }]}
            >
              <Input placeholder="Pretty Name" />
            </Form.Item>

            <Form.Item
              label="Name of Location"
              name="name"
              rules={[{ required: true, message: 'Please input name!' }]}
            >
              <Input placeholder="Name" />
            </Form.Item>

            <Form.Item label="Capabilities" name="capabilities">
              <CapabilitiesSelect placeholder="Select capabilities" />
            </Form.Item>

            <Form.Item
              label="Station Mode"
              name="station_mode"
              tooltip="How this location runs its checkout or return experience. Leave blank to keep using the corporate client's scanner config, as before. Options are limited to what this location's capabilities allow."
            >
              <Select
                style={{ width: '100%' }}
                placeholder="Use the corporate client's scanner config"
                allowClear
              >
                {Object.entries(STATION_MODE_LABELS)
                  .filter(([value]) =>
                    STATION_MODE_REQUIRED_CAPABILITY_NAMES[
                      value as keyof typeof STATION_MODE_REQUIRED_CAPABILITY_NAMES
                    ].every((name) => selectedCapabilityNames.has(name)),
                  )
                  .map(([value, label]) => (
                    <Option key={value} value={value}>
                      {label}
                    </Option>
                  ))}
              </Select>
            </Form.Item>

            <Form.Item
              label="Status"
              name="status_id"
              rules={[{ required: true, message: 'Please select status!' }]}
            >
              <LocationStatusSelect placeholder="Select status" />
            </Form.Item>

            <Form.Item
              label="Menu Url"
              name="menu_url"
              rules={[{ type: 'url', message: 'Please provide valid URL' }]}
            >
              <Input inputMode="url" placeholder="Menu Url" />
            </Form.Item>

            <Form.Item
              label="Track Inventory"
              name="track_inventory"
              valuePropName="checked"
            >
              <Checkbox />
            </Form.Item>

            <Form.Item
              label="Low Container Threshold"
              name="low_cup_threshold"
              rules={[{ required: true, message: 'Please input Threshold!' }]}
            >
              <Input
                type="number"
                placeholder="Input Threshold (default: 10)"
              />
            </Form.Item>

            <Form.Item
              label="Belongs to Corporate Client"
              name="corporate_client_id"
            >
              <CorporateClientSelect
                placeholder="Select Corporate Client"
                allowClear
                clients={
                  dbUser?.isCorporateAdmin
                    ? dbUser?.userCorporateClients
                    : undefined
                }
              />
            </Form.Item>
            <Form.Item
              label="Timezone"
              name="timezone"
              rules={[{ required: true, message: 'Please select timezone!' }]}
            >
              <Select style={{ width: '100%' }} placeholder="Select Timezone">
                {timezones.map((t: any) => (
                  <Option key={t.name} value={t.tzCode}>
                    {t.label}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            {mode === 'update' && dbUser?.role_id === AdminRoles.SuperAdmin && (
              <Form.Item
                label="Stripe Reader Serial"
                name="stripe_reader_serial"
                tooltip="Locks this station's checkout kiosk to only ever pair with this specific Stripe reader. Leave blank to allow it to connect to any nearby reader."
              >
                <Input placeholder="e.g. STRM26200123456" allowClear />
              </Form.Item>
            )}
          </Col>

          <Col span={24}>
            <Button type="primary" htmlType="submit" loading={loading}>
              {mode === 'create' ? 'Save' : 'Update'}
            </Button>
          </Col>
        </Row>
      </Form>
    </Row>
  );
};

export default LocationForm;
